import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, CalendarCheckIcon, PlayIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Field, inputClass, ConsentCheckbox } from '../ui/Field';
import { MeetingScheduler } from '../MeetingScheduler';
import { submitLead, requestDownloadUrl, validateEmail } from '../../lib/leads';
import { scoreRegistration } from '../../lib/webinarScore';
import { track } from '../../lib/analytics';
import { INSTITUTION_TYPES, SENIORITY, FUNCTIONS, CHALLENGES, TIMELINES, DECISION_ROLES, REFERRAL_SOURCES, COUNTRY_GROUPS, isPersonalEmail } from '../../data/webinarForm';
import type { Webinar } from '../../data/webinars';
import { sessionDateLabel } from './SessionTime';
const selectClass = `${inputClass} appearance-none`;
interface RegistrationFormProps {
  webinar: Webinar;
  /** 'register' books a place on an upcoming session. 'replay' unlocks a recording. */
  mode: 'register' | 'replay';
  /** Called with the signed recording URL once a replay is unlocked. */
  onReplayReady?: (url: string) => void;
}

/**
 * One qualifying form, used for both registration and replay access.
 *
 * Every question earns its place by changing what happens next: the answers set
 * the lead score, the lane and the follow up offer. The session is prefilled and
 * shown back to the registrant rather than asked for.
 */
export function RegistrationForm({
  webinar,
  mode,
  onReplayReady
}: RegistrationFormProps) {
  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [institution, setInstitution] = React.useState('');
  const [institutionType, setInstitutionType] = React.useState('');
  const [country, setCountry] = React.useState('');
  const [jobTitle, setJobTitle] = React.useState('');
  const [seniority, setSeniority] = React.useState('');
  const [functionArea, setFunctionArea] = React.useState('');
  const [challenge, setChallenge] = React.useState('');
  const [takeaway, setTakeaway] = React.useState('');
  const [timeline, setTimeline] = React.useState('');
  const [decisionRole, setDecisionRole] = React.useState('');
  const [briefing, setBriefing] = React.useState(false);
  const [referral, setReferral] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [showScheduler, setShowScheduler] = React.useState(false);
  const [replayError, setReplayError] = React.useState<string | null>(null);
  const personal = email.includes('@') && isPersonalEmail(email);
  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }
    setSubmitting(true);
    const scored = scoreRegistration({
      email,
      institutionType,
      seniority,
      timeline,
      decisionRole,
      functionArea,
      briefingRequested: briefing
    });
    const result = await submitLead({
      email,
      firstName: fullName,
      organisation: institution,
      role: jobTitle,
      country,
      consent,
      source: mode === 'replay' ? `${webinar.hubspotSource}:replay` : webinar.hubspotSource,
      line: scored.followUpLine,
      pipeline: 'government',
      notes: takeaway,
      qualification: {
        webinar: webinar.title,
        webinarDate: webinar.date,
        institutionType,
        seniority,
        function: functionArea,
        challenge,
        timeline,
        decisionRole,
        briefingRequested: briefing,
        referralSource: referral,
        personalEmail: scored.personalEmail,
        leadScore: scored.total,
        lane: scored.lane,
        followUpLine: scored.followUpLine,
        hubspotList: webinar.hubspotList
      }
    });
    if (!result.ok) {
      setSubmitting(false);
      setError(result.error ?? 'We could not complete your registration. Please try again.');
      return;
    }
    if (mode === 'replay') {
      const download = await requestDownloadUrl(webinar.replayFile, {
        kind: 'resource',
        id: webinar.slug
      });
      if (download.url) {
        onReplayReady?.(download.url);
      } else {
        setReplayError(download.error ?? 'We could not prepare the recording. Please try again.');
      }
    }
    track(mode === 'replay' ? 'webinar_replay_unlocked' : 'webinar_registered', {
      webinar: webinar.slug,
      line: scored.followUpLine,
      lane: scored.lane,
      score: scored.total
    });
    setSubmitting(false);
    setDone(true);
    if (briefing) setShowScheduler(true);
  };
  if (done) {
    return <div className="rounded-md border border-line bg-panel p-6 sm:p-8" role="status" data-id="element-460" data-mp="3if3ii799mnis">
        <div className="flex items-center gap-2 text-gold" data-id="element-461" data-mp="3n63enaak91k8">
          {mode === 'replay' ? <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-462" data-mp="1br35ah0p40zh" /> : <CalendarCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-463" data-mp="1bp05545twrh1" />}
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-464" data-mp="16rzzgucw5qqg">
            {mode === 'replay' ? 'Recording unlocked' : 'Your place is reserved'}
          </span>
        </div>

        <h3 className="mt-4 font-display text-[23px] leading-snug text-ink" data-id="element-465" data-mp="1giuuan47dyni">
          {mode === 'replay' ? 'The recording is ready above.' : `We will see you on ${sessionDateLabel(webinar)}.`}
        </h3>

        <p className="mt-3 text-[16px] text-body" data-id="element-466" data-mp="12qocyxpbdlmh">
          {mode === 'replay' ? `We have also sent the one page summary to ${email}. The recording link is signed for you and expires after fifteen minutes.` : `A calendar invitation and the joining link are on their way to ${email}. We will remind you one week, one day and one hour before the session.`}
        </p>

        {replayError && <p role="alert" className="mt-4 text-[15px] text-[#FFAD9E]" data-id="element-467" data-mp="2gaq76s1wxlyn">
            {replayError}
          </p>}

        {briefing && <div className="mt-8" data-id="element-468" data-mp="2l76x9rsoaaed">
            <h4 className="font-display text-[19px] text-ink" data-id="element-469" data-mp="2q8e3hmf631ix">Book your private briefing</h4>
            <p className="mt-2 text-[15px] text-body" data-id="element-470" data-mp="2v77ffaet2kgz">
              Pick a time that suits your team. We will come to it having read your answers, so the session
              starts on your situation rather than on our introduction.
            </p>
            {showScheduler && <MeetingScheduler className="mt-5" line="gov" data-id="element-471" data-mp="22ithsvrmpq5y" />}
          </div>}
      </div>;
  }
  return <form onSubmit={onSubmit} className="rounded-md border border-line bg-panel p-6 sm:p-8" noValidate data-id="element-472" data-mp="27fsbjd7drv3m">
      <fieldset disabled={submitting} className="space-y-6" data-id="element-473" data-mp="2ca683ixs489v">
        <legend className="sr-only" data-id="element-474" data-mp="1esfzhwyuj5ok">
          {mode === 'replay' ? 'Watch the recording' : 'Register for this session'}
        </legend>

        {/* The session, shown rather than asked for */}
        <div className="rounded-sm border border-line bg-champagne p-4" data-id="element-475" data-mp="vmow62zx4b7">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold" data-id="element-476" data-mp="3t0gxuj3ex7od">
            {mode === 'replay' ? 'Recording' : 'Session'}
          </p>
          <p className="mt-2 text-[15px] font-medium text-ink" data-id="element-477" data-mp="3o3gr05yqo0w7">{webinar.title}</p>
          <p className="mt-1 text-small text-muted" data-id="element-478" data-mp="3j4nas5s2hvsc">{sessionDateLabel(webinar)}</p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2" data-id="element-479" data-mp="3e4r94d6uegfy">
          <Field id="reg-name" label="Full name" required data-id="element-480" data-mp="39chjky9n3ie7">
            <input id="reg-name" type="text" required autoComplete="name" value={fullName} onChange={event => setFullName(event.target.value)} className={inputClass} data-id="element-481" data-mp="39awptav5g3he" />
          </Field>

          <Field id="reg-email" label="Official email" required hint={personal ? 'A personal address is fine. We may ask which institution you represent.' : undefined} data-id="element-482" data-mp="2veszvtrb6haq">
            <input id="reg-email" type="email" required autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="you@ministry.gov" className={inputClass} data-id="element-483" data-mp="n4s898c274n1" />
          </Field>
        </div>

        <div className="grid gap-5 sm:grid-cols-2" data-id="element-484" data-mp="3visgk3g4pntz">
          <Field id="reg-institution" label="Institution" required data-id="element-485" data-mp="45o0e2w97gwy">
            <input id="reg-institution" type="text" required autoComplete="organization" value={institution} onChange={event => setInstitution(event.target.value)} className={inputClass} data-id="element-486" data-mp="3mjdqh53vjfa0" />
          </Field>

          <Field id="reg-institution-type" label="Institution type" required data-id="element-487" data-mp="3hku2z09mtvfj">
            <select id="reg-institution-type" required value={institutionType} onChange={event => setInstitutionType(event.target.value)} className={selectClass} data-id="element-488" data-mp="2xyis6uj44c2z">
              <option value="" data-id="element-489" data-mp="37xhr3le2stcs">Please choose</option>
              {INSTITUTION_TYPES.map(option => <option key={option} value={option} data-id="element-490" data-mp="32wu3zasywwnu">
                  {option}
                </option>)}
            </select>
          </Field>
        </div>

        <div className="grid gap-5 sm:grid-cols-2" data-id="element-491" data-mp="3cwkxpa9pf7mv">
          <Field id="reg-country" label="Country" required data-id="element-492" data-mp="3ctoxtgeaq8vs">
            <select id="reg-country" required value={country} onChange={event => setCountry(event.target.value)} className={selectClass} data-id="element-493" data-mp="3qm4ve1ofhepb">
              <option value="" data-id="element-494" data-mp="4g7y3bdzl1q7">Please choose</option>
              {COUNTRY_GROUPS.map(group => <optgroup key={group.region} label={group.region} data-id="element-495" data-mp="3vo3xz9ccg7l7">
                  {group.countries.map(name => <option key={name} value={name} data-id="element-496" data-mp="8q45svoh7z2y">
                      {name}
                    </option>)}
                </optgroup>)}
            </select>
          </Field>

          <Field id="reg-title" label="Job title" required data-id="element-497" data-mp="3r28vhrn3pka8">
            <input id="reg-title" type="text" required autoComplete="organization-title" value={jobTitle} onChange={event => setJobTitle(event.target.value)} className={inputClass} data-id="element-498" data-mp="3r43l3j9bi0kn" />
          </Field>
        </div>

        <div className="grid gap-5 sm:grid-cols-2" data-id="element-499" data-mp="ioa5d6mt8t4n">
          <Field id="reg-seniority" label="Seniority" required data-id="element-500" data-mp="dmkqffwgvvba">
            <select id="reg-seniority" required value={seniority} onChange={event => setSeniority(event.target.value)} className={selectClass} data-id="element-501" data-mp="33cepnu178vzh">
              <option value="" data-id="element-502" data-mp="1dwlw06m1gino">Please choose</option>
              {SENIORITY.map(option => <option key={option} value={option} data-id="element-503" data-mp="1ivoybjamwary">
                  {option}
                </option>)}
            </select>
          </Field>

          <Field id="reg-function" label="Function" required data-id="element-504" data-mp="1rmxmy26w0bd8">
            <select id="reg-function" required value={functionArea} onChange={event => setFunctionArea(event.target.value)} className={selectClass} data-id="element-505" data-mp="1ro8qc0wf7h3t">
              <option value="" data-id="element-506" data-mp="26k85twlv0jiz">Please choose</option>
              {FUNCTIONS.map(option => <option key={option} value={option} data-id="element-507" data-mp="2altdn3jje7mb">
                  {option}
                </option>)}
            </select>
          </Field>
        </div>

        <Field id="reg-challenge" label="Biggest challenge right now" required data-id="element-508" data-mp="c808ydpz9skk">
          <select id="reg-challenge" required value={challenge} onChange={event => setChallenge(event.target.value)} className={selectClass} data-id="element-509" data-mp="g8an4dnxbpai">
            <option value="" data-id="element-510" data-mp="1elfzuzyl76t3">Please choose</option>
            {CHALLENGES.map(option => <option key={option} value={option} data-id="element-511" data-mp="1skpehv759suy">
                {option}
              </option>)}
          </select>
        </Field>

        <Field id="reg-takeaway" label="What are you hoping to take away" hint="We shape the session around what registrants tell us here." data-id="element-512" data-mp="22h0qek2togov">
          <textarea id="reg-takeaway" rows={3} value={takeaway} onChange={event => setTakeaway(event.target.value)} className={inputClass} data-id="element-513" data-mp="2c7djx1sm7k75" />
        </Field>

        <div className="grid gap-5 sm:grid-cols-2" data-id="element-514" data-mp="2l7a52tfsq0xg">
          <Field id="reg-timeline" label="Exploring a project or system in the next 6 to 12 months" required data-id="element-515" data-mp="2l5yx0hfbi70j">
            <select id="reg-timeline" required value={timeline} onChange={event => setTimeline(event.target.value)} className={selectClass} data-id="element-516" data-mp="2zvsau9gr0wbb">
              <option value="" data-id="element-517" data-mp="2qtssndtzzm6s">Please choose</option>
              {TIMELINES.map(option => <option key={option} value={option} data-id="element-518" data-mp="2utuk6u60qz8c">
                  {option}
                </option>)}
            </select>
          </Field>

          <Field id="reg-decision" label="Your role in that decision" required data-id="element-519" data-mp="2lv92f18dfo8m">
            <select id="reg-decision" required value={decisionRole} onChange={event => setDecisionRole(event.target.value)} className={selectClass} data-id="element-520" data-mp="276czfil691ev">
              <option value="" data-id="element-521" data-mp="1y6pwgscsnnjp">Please choose</option>
              {DECISION_ROLES.map(option => <option key={option} value={option} data-id="element-522" data-mp="2336ldorti5xu">
                  {option}
                </option>)}
            </select>
          </Field>
        </div>

        <Field id="reg-referral" label="How did you hear about us" data-id="element-523" data-mp="1q477za5c4niz">
          <select id="reg-referral" value={referral} onChange={event => setReferral(event.target.value)} className={selectClass} data-id="element-524" data-mp="1q71rmzgiy4bc">
            <option value="" data-id="element-525" data-mp="1haknig6kv6p3">Please choose</option>
            {REFERRAL_SOURCES.map(option => <option key={option} value={option} data-id="element-526" data-mp="1cay05dx1ca2k">
                {option}
              </option>)}
          </select>
        </Field>

        <div className="space-y-4 border-t border-line pt-6" data-id="element-527" data-mp="12g6268zjjl79">
          <ConsentCheckbox id="reg-briefing" checked={briefing} onChange={setBriefing} data-id="element-528" data-mp="ydj77z7kpk5u">
            A private briefing for my team would be useful. Ticking this opens our diary as soon as you
            submit.
          </ConsentCheckbox>

          <ConsentCheckbox id="reg-consent" checked={consent} onChange={setConsent} data-id="element-529" data-mp="2sslnr7en9rwm">
            I am happy for Cavalla Solution to send me the joining link, reminders and related public sector
            material. See our{' '}
            <Link to="/legal/privacy" className="underline underline-offset-2" data-id="element-530" data-mp="jh1fhl0u8ecj">
              privacy notice
            </Link>
            .
          </ConsentCheckbox>
        </div>

        {error && <p role="alert" className="text-[15px] text-[#FFAD9E]" data-id="element-531" data-mp="sdafp1wn3gzq">
            {error}
          </p>}

        <Button type="submit" size="lg" disabled={submitting} data-id="element-532" data-mp="3rsny6mszydjb">
          {submitting ? 'Submitting' : mode === 'replay' ? 'Watch the recording' : 'Reserve my place'}
        </Button>

        <p className="flex items-start gap-2 text-small text-muted" data-id="element-533" data-mp="9kyh8q93wzuv">
          <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" aria-hidden="true" data-id="element-534" data-mp="1g0scjburh59k" />
          Your details reach our team only. Personal data is held in the European Union, and you can withdraw
          consent at any time.
        </p>
      </fieldset>
    </form>;
}